import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import AiAnswerGenerator from './AiAnswerGenerator';
import { ToastContainer, toast } from 'react-toastify';
import { Row, Col, Table, Modal, Button, Input, Select, Dropdown, Menu } from 'antd';
import axios from '../components/Axios';
import { EyeIcon, BotIcon, PlusIcon, MinusIcon, Search, X, SlidersHorizontal, ChevronDown, ArrowUpDown, Download } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import TurndownService from 'turndown';
import * as XLSX from 'xlsx';

const { TextArea } = Input;
const { Option } = Select;

const turndown = new TurndownService({ headingStyle: 'atx', bulletListMarker: '-' });

const markdownToHtml = (md) => {
  if (!md) return '';
  return md
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[hul])(.+)$/gm, '<p>$1</p>');
};

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet'];

// ─── Parse views/upvotes number from string ───────────────────────────────────
const parseNum = (str) => {
  if (!str) return 0;
  const s = String(str).toLowerCase().replace(/,/g, '').trim();
  // Match number with optional k/m suffix e.g. "2.5k", "1.2m", "500"
  const match = s.match(/^([\d.]+)\s*([km])/);
  if (match) {
    const n = parseFloat(match[1]);
    if (match[2] === 'k') return n * 1000;
    if (match[2] === 'm') return n * 1000000;
  }
  // No suffix — just parse the leading number
  const plain = s.match(/^[\d.]+/);
  return plain ? parseFloat(plain[0]) : 0;
};

const getRecordViews = (record) => {
  if (!record.answers?.length) return 0;
  const vals = record.answers.map(a => {
    // e.g. "2.5k views" or "12,345 views"
    const m = (a.views || '').match(/([\d.,]+[kKmM]?)\s*views?/i);
    return m ? parseNum(m[1]) : 0;
  });
  return Math.max(...vals);
};

const getRecordUpvotes = (record) => {
  if (!record.answers?.length) return 0;
  const vals = record.answers.map(a => {
    // Try dedicated upvotes field first
    if (a.upvotes) return parseNum(a.upvotes);
    // Fallback: second number in views string
    const nums = (a.views || '').match(/\d[\d.,]*[kKmM]?/g);
    return nums?.[1] ? parseNum(nums[1]) : 0;
  });
  return Math.max(...vals);
};

// ─── Filter Bar Styles ────────────────────────────────────────────────────────
const filterStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');

  .qd-filter-bar {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 16px;
    font-family: 'DM Sans', sans-serif;
  }

  .qd-filter-top {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .qd-filter-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    white-space: nowrap;
  }

  .qd-filter-divider {
    width: 1px;
    height: 28px;
    background: #e5e7eb;
    flex-shrink: 0;
  }

  .qd-keyword-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    flex: 1;
  }

  .qd-keyword-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 160px;
    max-width: 260px;
  }

  .qd-keyword-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    font-weight: 600;
    padding: 3px 7px;
    border-radius: 5px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .qd-badge-include {
    background: #dcfce7;
    color: #166534;
    border: 1px solid #bbf7d0;
  }

  .qd-badge-exclude {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }

  .qd-keyword-input {
    width: 100%;
    height: 34px;
    padding: 0 30px 0 10px;
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    color: #1f2937;
    outline: none;
    background: #f9fafb;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  }

  .qd-keyword-input.include:focus {
    border-color: #22c55e;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(34,197,94,0.1);
  }

  .qd-keyword-input.exclude:focus {
    border-color: #ef4444;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(239,68,68,0.1);
  }

  .qd-keyword-input::placeholder {
    color: #9ca3af;
  }

  .qd-clear-btn {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    cursor: pointer;
    color: #9ca3af;
    padding: 0;
    display: flex;
    align-items: center;
    transition: color 0.15s;
  }

  .qd-clear-btn:hover { color: #374151; }

  .qd-select-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .qd-select-label {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    white-space: nowrap;
  }

  .qd-native-select {
    height: 34px;
    padding: 0 28px 0 10px;
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    font-size: 12.5px;
    font-family: 'DM Sans', sans-serif;
    color: #374151;
    background: #f9fafb;
    cursor: pointer;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;
    transition: border-color 0.15s, box-shadow 0.15s;
    min-width: 130px;
  }

  .qd-native-select:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
    background-color: #fff;
  }

  .qd-reset-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    height: 34px;
    padding: 0 12px;
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    font-size: 12.5px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    color: #6b7280;
    background: #f9fafb;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }

  .qd-reset-btn:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #dc2626;
  }

  .qd-results-strip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #f3f4f6;
  }

  .qd-results-count {
    font-size: 12px;
    color: #6b7280;
    font-family: 'DM Sans', sans-serif;
  }

  .qd-results-count strong {
    color: #111827;
    font-weight: 600;
  }

  .qd-active-tags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .qd-active-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 20px;
    font-family: 'DM Sans', sans-serif;
  }

  .qd-active-tag.include { background: #dcfce7; color: #166534; }
  .qd-active-tag.exclude { background: #fee2e2; color: #991b1b; }
  .qd-active-tag.sort    { background: #fef9c3; color: #713f12; }
  .qd-active-tag.ai      { background: #ede9fe; color: #5b21b6; }
  .qd-active-tag.filter  { background: #ede9fe; color: #5b21b6; }

  .qd-badge-sort {
    background: #fef9c3;
    color: #713f12;
    border: 1px solid #fde68a;
  }

  .qd-badge-ai {
    background: #ede9fe;
    color: #5b21b6;
    border: 1px solid #ddd6fe;
  }

  .qd-tag-x {
    cursor: pointer;
    opacity: 0.6;
    display: flex;
    align-items: center;
  }
  .qd-tag-x:hover { opacity: 1; }
`;

// ─── Inline-editable expanded row ────────────────────────────────────────────
const ExpandedAnswerRow = ({ record, questionDocId, onEditedContentChange }) => {
  const savedContent = record.generatedAnswer?.generatedContent || '';
  const [htmlContent, setHtmlContent] = useState(() => markdownToHtml(savedContent));
  const [savedHtmlRef, setSavedHtmlRef] = useState(() => markdownToHtml(savedContent));
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const fresh = record.generatedAnswer?.generatedContent || '';
    const freshHtml = markdownToHtml(fresh);
    setHtmlContent(freshHtml);
    setSavedHtmlRef(freshHtml);
    onEditedContentChange(record._id, fresh);
  }, [record.generatedAnswer?.generatedContent]);

  const handleQuillChange = (html) => {
    setHtmlContent(html);
    onEditedContentChange(record._id, turndown.turndown(html));
  };

  const handleSave = async () => {
    const markdownToSave = turndown.turndown(htmlContent);
    setSaving(true);
    try {
      await axios.put(`/api/questions/${questionDocId}/generated-answer`, {
        generatedContent: markdownToSave,
        questionLink: record.questionLink,
      });
      setSavedHtmlRef(htmlContent);
      onEditedContentChange(record._id, markdownToSave);
      toast.success('Answer saved!');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save answer');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setHtmlContent(savedHtmlRef);
    onEditedContentChange(record._id, turndown.turndown(savedHtmlRef));
    setIsEditing(false);
  };

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BotIcon size={16} style={{ color: '#52c41a' }} />
          <span style={{ fontWeight: '600', color: '#1f2937' }}>AI Generated Answer</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {record.generatedAnswer?.source && (
            <span style={{ fontSize: '12px', color: '#666', background: '#f0f0f0', padding: '2px 8px', borderRadius: '4px' }}>
              {record.generatedAnswer.source}
            </span>
          )}
          <Button size="small" onClick={() => { navigator.clipboard.writeText(turndown.turndown(htmlContent)); toast.success('Copied!'); }}>Copy</Button>
          {!isEditing && (
            <Button size="small" onClick={() => setIsEditing(true)} style={{ borderColor: '#1890ff', color: '#1890ff' }}>✏️ Edit</Button>
          )}
        </div>
      </div>

      {!isEditing && (
        <div style={{ border: '1px solid #e8e8e8', borderRadius: '8px', padding: '20px 24px', backgroundColor: '#fafafa', maxHeight: '420px', overflowY: 'auto', lineHeight: '1.8', fontSize: '14.5px', color: '#1f2937' }}>
          <ReactMarkdown
            components={{
              h1: ({node, ...p}) => <h1 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 14px', color: '#111827' }} {...p} />,
              h2: ({node, ...p}) => <h2 style={{ fontSize: '19px', fontWeight: '600', margin: '18px 0 10px', color: '#1f2937' }} {...p} />,
              h3: ({node, ...p}) => <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '14px 0 8px', color: '#374151' }} {...p} />,
              p:  ({node, ...p}) => <p style={{ margin: '0 0 12px', lineHeight: '1.8' }} {...p} />,
              ul: ({node, ...p}) => <ul style={{ margin: '0 0 12px', paddingLeft: '22px' }} {...p} />,
              ol: ({node, ...p}) => <ol style={{ margin: '0 0 12px', paddingLeft: '22px' }} {...p} />,
              li: ({node, ...p}) => <li style={{ marginBottom: '6px', lineHeight: '1.7' }} {...p} />,
              strong: ({node, ...p}) => <strong style={{ fontWeight: '600', color: '#111827' }} {...p} />,
              em: ({node, ...p}) => <em style={{ fontStyle: 'italic', color: '#374151' }} {...p} />,
              code: ({node, ...p}) => <code style={{ backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '13px', color: '#0f172a' }} {...p} />,
              pre: ({node, ...p}) => <pre style={{ backgroundColor: '#f1f5f9', padding: '14px', borderRadius: '6px', overflow: 'auto', marginBottom: '12px', fontSize: '13px' }} {...p} />,
              blockquote: ({node, ...p}) => <blockquote style={{ borderLeft: '3px solid #d1d5db', paddingLeft: '14px', color: '#6b7280', margin: '0 0 12px', fontStyle: 'italic' }} {...p} />,
            }}
          >
            {savedContent}
          </ReactMarkdown>
        </div>
      )}

      {isEditing && (
        <div style={{ marginBottom: '10px' }}>
          <ReactQuill theme="snow" value={htmlContent} onChange={handleQuillChange} modules={QUILL_MODULES} formats={QUILL_FORMATS} style={{ backgroundColor: '#fff', borderRadius: '6px', fontSize: '14.5px' }} />
        </div>
      )}

      {isEditing && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
          <Button type="primary" size="small" loading={saving} onClick={handleSave} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>Save</Button>
          <Button size="small" onClick={handleCancel} disabled={saving}>Cancel</Button>
        </div>
      )}

      {(record.generatedAnswer?.selectedKeyword || record.generatedAnswer?.selectedCta) && (
        <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#f0f9ff', borderRadius: '4px', fontSize: '12px' }}>
          {record.generatedAnswer.selectedKeyword && <div><strong>Keyword:</strong> {record.generatedAnswer.selectedKeyword}</div>}
          {record.generatedAnswer.selectedCta && <div><strong>CTA:</strong> {record.generatedAnswer.selectedCta}</div>}
        </div>
      )}
    </div>
  );
};

// ─── Filter Bar Component ─────────────────────────────────────────────────────
const FilterBar = ({ total, filtered, filters, onChange, onReset }) => {
  const hasAnyFilter =
    filters.includeKeyword || filters.excludeKeyword ||
    filters.aiStatus !== 'all' ||
    filters.sortBy !== 'none';

  return (
    <div className="qd-filter-bar">
      <div className="qd-filter-top">

        {/* Label */}
        <div className="qd-filter-label">
          <SlidersHorizontal size={14} />
          Filters
        </div>

        <div className="qd-filter-divider" />

        {/* Include keyword */}
        <div className="qd-keyword-group">
          <span className="qd-keyword-badge qd-badge-include">✓ Include</span>
          <div className="qd-keyword-input-wrap">
            <input
              className="qd-keyword-input include"
              type="text"
              placeholder="Keywords to include..."
              value={filters.includeKeyword}
              onChange={e => onChange('includeKeyword', e.target.value)}
            />
            {filters.includeKeyword && (
              <button className="qd-clear-btn" onClick={() => onChange('includeKeyword', '')}>
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <div className="qd-filter-divider" />

        {/* Exclude keyword */}
        <div className="qd-keyword-group">
          <span className="qd-keyword-badge qd-badge-exclude">✕ Exclude</span>
          <div className="qd-keyword-input-wrap">
            <input
              className="qd-keyword-input exclude"
              type="text"
              placeholder="Keywords to exclude..."
              value={filters.excludeKeyword}
              onChange={e => onChange('excludeKeyword', e.target.value)}
            />
            {filters.excludeKeyword && (
              <button className="qd-clear-btn" onClick={() => onChange('excludeKeyword', '')}>
                <X size={12} />
              </button>
            )}
          </div>
        </div>

        <div className="qd-filter-divider" />

        {/* Sort — comes BEFORE AI Answer */}
        <div className="qd-select-wrap">
          <span className="qd-keyword-badge qd-badge-sort">↕ Sort</span>
          <select
            className="qd-native-select"
            value={filters.sortBy}
            onChange={e => onChange('sortBy', e.target.value)}
          >
            <option value="none">Default</option>
            <option value="views_desc">Views ↑</option>
            <option value="views_asc">Views ↓</option>
            <option value="upvotes_desc">Upvotes ↑</option>
            <option value="upvotes_asc">Upvotes ↓</option>
          </select>
        </div>

        <div className="qd-filter-divider" />

        {/* AI Status — comes AFTER Sort */}
        <div className="qd-select-wrap">
          <span className="qd-keyword-badge qd-badge-ai">🤖 AI Answer</span>
          <select
            className="qd-native-select"
            value={filters.aiStatus}
            onChange={e => onChange('aiStatus', e.target.value)}
          >
            <option value="all">All</option>
            <option value="generated">✅ Generated</option>
            <option value="not_generated">❌ Not Yet</option>
          </select>
        </div>

        {/* Reset */}
        {hasAnyFilter && (
          <button className="qd-reset-btn" onClick={onReset}>
            <X size={12} /> Reset
          </button>
        )}
      </div>

      {/* Results strip */}
      <div className="qd-results-strip">
        <div className="qd-results-count">
          Showing <strong>{filtered}</strong> of <strong>{total}</strong> questions
        </div>

        {/* Active filter tags */}
        <div className="qd-active-tags">
          {filters.includeKeyword && (
            <span className="qd-active-tag include">
              ✓ "{filters.includeKeyword}"
              <span className="qd-tag-x" onClick={() => onChange('includeKeyword', '')}><X size={10} /></span>
            </span>
          )}
          {filters.excludeKeyword && (
            <span className="qd-active-tag exclude">
              ✕ "{filters.excludeKeyword}"
              <span className="qd-tag-x" onClick={() => onChange('excludeKeyword', '')}><X size={10} /></span>
            </span>
          )}
          {filters.sortBy !== 'none' && (
            <span className="qd-active-tag sort">
              ↕ {filters.sortBy.replace('_desc', ' ↑').replace('_asc', ' ↓')}
              <span className="qd-tag-x" onClick={() => onChange('sortBy', 'none')}><X size={10} /></span>
            </span>
          )}
          {filters.aiStatus !== 'all' && (
            <span className="qd-active-tag ai">
              {filters.aiStatus === 'generated' ? '✅ Generated' : '❌ Not Yet'}
              <span className="qd-tag-x" onClick={() => onChange('aiStatus', 'all')}><X size={10} /></span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
function QuestionDetails() {
  const location = useLocation();
  const { questionData } = location.state || {};

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedContent, setSelectedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [editedContentMap, setEditedContentMap] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedQuestionForAI, setSelectedQuestionForAI] = useState(null);

  const [filters, setFilters] = useState({
    includeKeyword: '',
    excludeKeyword: '',
    aiStatus: 'all',
    sortBy: 'none',
  });

  const storedUser = localStorage.getItem("user");
  const userId = storedUser ? JSON.parse(storedUser)._id : null;

  const fetchQuestionDetails = async () => {
    setLoading(true);
    const toastId = toast.loading("Loading questions...");
    try {
      const response = await axios.get(`/api/question-details/${questionData._id}/${userId}`);
      setQuestions(response.data.questions);
      toast.update(toastId, { render: "Questions loaded!", type: "success", isLoading: false, autoClose: 3000 });
    } catch (error) {
      console.error('Error fetching questions:', error);
      toast.update(toastId, { render: "Failed to load questions", type: "error", isLoading: false, autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (questionData?._id && userId) fetchQuestionDetails();
  }, [questionData?._id, userId]);

  useEffect(() => {
    if (selectedRowKeys.length > 0) {
      const latestId = selectedRowKeys[selectedRowKeys.length - 1];
      const latest = questions.find(q => q._id === latestId);
      setSelectedQuestionForAI(latest?.answers?.length ? latest : null);
    } else {
      setSelectedQuestionForAI(null);
    }
  }, [selectedRowKeys, questions]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({ includeKeyword: '', excludeKeyword: '', aiStatus: 'all', sortBy: 'none' });
  };

  // ── Apply filters + sort ──────────────────────────────────────────────────
  const filteredQuestions = useMemo(() => {
    let result = [...questions];

    // Include keyword filter
    if (filters.includeKeyword.trim()) {
      const kw = filters.includeKeyword.trim().toLowerCase();
      result = result.filter(q =>
        (q.questionTitle || q.questionLink || '').toLowerCase().includes(kw)
      );
    }

    // Exclude keyword filter
    if (filters.excludeKeyword.trim()) {
      const kw = filters.excludeKeyword.trim().toLowerCase();
      result = result.filter(q =>
        !(q.questionTitle || q.questionLink || '').toLowerCase().includes(kw)
      );
    }

    // AI status filter
    if (filters.aiStatus === 'generated') {
      result = result.filter(q => !!q.generatedAnswer?.generatedContent);
    } else if (filters.aiStatus === 'not_generated') {
      result = result.filter(q => !q.generatedAnswer?.generatedContent);
    }

    // Sort
    if (filters.sortBy === 'views_desc') {
      result.sort((a, b) => getRecordViews(b) - getRecordViews(a));      // highest first
    } else if (filters.sortBy === 'views_asc') {
      result.sort((a, b) => getRecordViews(a) - getRecordViews(b));      // lowest first
    } else if (filters.sortBy === 'upvotes_desc') {
      result.sort((a, b) => getRecordUpvotes(b) - getRecordUpvotes(a));  // highest first
    } else if (filters.sortBy === 'upvotes_asc') {
      result.sort((a, b) => getRecordUpvotes(a) - getRecordUpvotes(b));  // lowest first
    }

    return result;
  }, [questions, filters]);
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // ── Export helpers ────────────────────────────────────────────────────────
  const buildExportRows = (questionsToExport) => {
    return questionsToExport.map((q, idx) => {
      const firstAnswer = q.answers?.[0];
      const viewsMatch = (firstAnswer?.views || '').match(/([\d.,]+[kKmM]?)\s*views?/i);
      const views = viewsMatch ? viewsMatch[1] : '';
      const numsInViews = (firstAnswer?.views || '').match(/\d[\d.,]*[kKmM]?/g);
      const upvotes = firstAnswer?.upvotes || numsInViews?.[1] || '';

      return {
        '#': idx + 1,
        'Question Title': q.questionTitle || '',
        'Question Link': q.questionLink || '',
        'Views': views,
        'Upvotes': upvotes,
        'Answers Count': q.answers?.length || 0,
        'AI Answer Generated': q.generatedAnswer?.generatedContent ? 'Yes' : 'No',
        'AI Answer': q.generatedAnswer?.generatedContent || '',
        'AI Source': q.generatedAnswer?.source || '',
        'Selected Keyword': q.generatedAnswer?.selectedKeyword || '',
        'Selected CTA': q.generatedAnswer?.selectedCta || '',
      };
    });
  };

  const handleExportCSV = () => {
    const dataToExport = selectedRowKeys.length > 0
      ? filteredQuestions.filter(q => selectedRowKeys.includes(q._id))
      : filteredQuestions;

    if (!dataToExport.length) {
      toast.warning('No questions to export');
      return;
    }

    const rows = buildExportRows(dataToExport);
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${questionData?.keyword || 'questions'}_export_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`✅ Exported ${dataToExport.length} questions as CSV`);
  };

  const handleExportExcel = () => {
    const dataToExport = selectedRowKeys.length > 0
      ? filteredQuestions.filter(q => selectedRowKeys.includes(q._id))
      : filteredQuestions;

    if (!dataToExport.length) {
      toast.warning('No questions to export');
      return;
    }

    const rows = buildExportRows(dataToExport);
    const worksheet = XLSX.utils.json_to_sheet(rows);

    // Auto column widths
    const colWidths = Object.keys(rows[0] || {}).map(key => ({
      wch: Math.min(Math.max(key.length, ...rows.map(r => String(r[key] || '').length).slice(0, 50)), 80) + 2
    }));
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions');
    XLSX.writeFile(workbook, `${questionData?.keyword || 'questions'}_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`✅ Exported ${dataToExport.length} questions as Excel`);
  };

  const handleEditedContentChange = (rowId, newText) => {
    setEditedContentMap(prev => ({ ...prev, [rowId]: newText }));
  };

  const expandedRowRender = (record) => {
    const hasAnswer = record.generatedAnswer?.generatedContent;
    if (!hasAnswer) {
      return <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>No AI answer generated yet</div>;
    }
    return <ExpandedAnswerRow record={record} questionDocId={questionData._id} onEditedContentChange={handleEditedContentChange} />;
  };

  const cc = { display: 'flex', justifyContent: 'center', alignItems: 'center' };

  // ── Highlight matching text ───────────────────────────────────────────────
  const highlightText = (text, include, exclude) => {
    if (!text) return text;
    if (!include) return text;
    const idx = text.toLowerCase().indexOf(include.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: '#fef08a', color: '#713f12', padding: '0 2px', borderRadius: '3px', fontWeight: '600' }}>
          {text.slice(idx, idx + include.length)}
        </mark>
        {text.slice(idx + include.length)}
      </>
    );
  };

  const columns = [
    {
      title: 'Question',
      dataIndex: 'questionLink',
      key: 'questionLink',
      width: 400,
      render: (link, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937', lineHeight: '1.45', textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: '5px' }}
            onMouseEnter={e => e.currentTarget.querySelector('span').style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.querySelector('span').style.textDecoration = 'none'}
          >
            <span style={{ flex: 1 }}>
              {highlightText(record.questionTitle || link, filters.includeKeyword)}
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '3px' }}>
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
          {record.questionTitle && (
            <span style={{ fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '360px' }}>{link}</span>
          )}
        </div>
      ),
    },
    {
  title: <div style={cc}>Views</div>,
  key: 'views',
  width: 80,
  render: (_, record) => {
    if (!record.answers?.length) return null;
    return record.answers.map((a, i) => {
      const match = (a.views || '').match(/(\d+\.?\d*[kKmM]?)\s*views/);
      return <div key={i} style={{ ...cc, padding: '2px 0', fontSize: '12px' }}>{match ? match[1] : ''}</div>;
    });
  },
},
{
  title: <div style={cc}>Upvotes</div>,
  key: 'upvotes',
  width: 80,
  render: (_, record) => {
    if (!record.answers?.length) return null;
    return record.answers.map((a, i) => {
      const nums = (a.views || '').match(/\d+/g);
      return <div key={i} style={{ ...cc, padding: '2px 0', fontSize: '12px' }}>{nums?.[1] || ''}</div>;
    });
  },
},
    {
      title: <div style={cc}>Answers</div>,
      key: 'answers',
      width: 80,
      render: (_, record) => (
        record.answers?.length ? (
          record.answers.map((a, i) => (
            <div key={i} style={{ ...cc, padding: '2px 0' }}>
              <EyeIcon size={16} onClick={() => { setSelectedContent(a.content); setIsModalVisible(true); }} style={{ cursor: 'pointer', color: '#1890ff' }} title="View Answer" />
            </div>
          ))
        ) : <div style={cc}>-</div>
      ),
    },
    {
      title: <div style={cc}>Answer</div>,
      key: 'aiStatus',
      width: 110,
      render: (_, record) => {
        const has = record.generatedAnswer?.generatedContent;
        return (
          <div style={cc}>
            {has ? (
              <span style={{ color: '#16a34a', fontWeight: '500', fontSize: '13px' }}>✅ Generated</span>
            ) : (
              <span style={{ color: '#9ca3af', fontWeight: '500', fontSize: '13px' }}>❌ Not Yet</span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <style>{filterStyles}</style>
      {/* <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" /> */}

      <div style={{ backgroundColor: '#f9fafb' }}>
        <Header />
        <div style={{ display: 'flex' }}>
          <Sidebar />
          <main style={{ flex: 1, padding: '30px' }}>
            <div className="subMainContainer">

              <div className="headerCommom" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className='heading'>{questionData.keyword}</div>
                <Dropdown
                  overlay={
                    <Menu>
                      <Menu.Item
                        key="csv"
                        icon={<Download size={13} style={{ marginRight: 4 }} />}
                        onClick={handleExportCSV}
                      >
                        Export as CSV
                        {selectedRowKeys.length > 0 && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: '#888' }}>
                            ({selectedRowKeys.length} selected)
                          </span>
                        )}
                      </Menu.Item>
                      <Menu.Item
                        key="excel"
                        icon={<Download size={13} style={{ marginRight: 4 }} />}
                        onClick={handleExportExcel}
                      >
                        Export as Excel (.xlsx)
                        {selectedRowKeys.length > 0 && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: '#888' }}>
                            ({selectedRowKeys.length} selected)
                          </span>
                        )}
                      </Menu.Item>
                    </Menu>
                  }
                  trigger={['click']}
                  placement="bottomRight"
                >
                  <Button
                    icon={<Download size={14} />}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      borderColor: '#d1d5db',
                      color: '#374151',
                      fontWeight: 500,
                      fontSize: 13,
                    }}
                  >
                    Export
                    <ChevronDown size={13} />
                  </Button>
                </Dropdown>
              </div>

              <AiAnswerGenerator
                userId={userId}
                questionData={questionData}
                questions={questions}
                selectedQuestionFromTable={selectedQuestionForAI}
                onRefreshData={fetchQuestionDetails}
                editedContentMap={editedContentMap}
              />

              {/* ── Filter Bar ── */}
              <FilterBar
                total={questions.length}
                filtered={filteredQuestions.length}
                filters={filters}
                onChange={handleFilterChange}
                onReset={handleResetFilters}
              />

              {/* Questions Table */}
              <Row justify="center">
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <div style={{ border: "1px solid #d9d9d9", borderRadius: "8px", paddingBottom: '22px', backgroundColor: '#fff' }}>
                    <Table
                      columns={columns}
                      loading={loading}
                      dataSource={filteredQuestions}
                      rowKey="_id"
                      pagination={{ pageSize: 20 }}
                      scroll={{ x: 700 }}
                      size="small"
                      rowSelection={rowSelection}
                      expandable={{
                        expandedRowRender,
                        expandIcon: ({ expanded, onExpand, record }) => {
                          const has = record.generatedAnswer?.generatedContent;
                          if (!has) return <span style={{ width: '16px', display: 'inline-block' }} />;
                          return expanded
                            ? <MinusIcon onClick={e => onExpand(record, e)} size={14} style={{ cursor: 'pointer' }} />
                            : <PlusIcon  onClick={e => onExpand(record, e)} size={14} style={{ cursor: 'pointer' }} />;
                        },
                        rowExpandable: (record) => !!record.generatedAnswer?.generatedContent,
                      }}
                      onRow={(record) => ({
                        style: {
                          backgroundColor: selectedQuestionForAI?._id === record._id ? '#f6ffed' : 'transparent',
                          border: selectedQuestionForAI?._id === record._id ? '2px solid #52c41a' : '1px solid transparent',
                        }
                      })}
                    />
                  </div>
                </Col>
              </Row>

            </div>
          </main>
        </div>
      </div>

      {/* Answer Modal */}
      <Modal
        title="Answer Content"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        footer={[<Button key="close" onClick={() => setIsModalVisible(false)}>Close</Button>]}
      >
        <div style={{ maxHeight: '500px', overflow: 'auto' }}>
          <ReactMarkdown>{selectedContent}</ReactMarkdown>
        </div>
      </Modal>
    </>
  );
}

export default QuestionDetails;